import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Heart } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ONBOARDING_BG = "https://static.prod-images.emergentagent.com/jobs/c1134d5f-8ffa-4e52-be31-05765f8e9179/images/cc97c84e6556ea56d5a7a0d8116ab3cb8215b97596013bb9bdabc9aa26e10a6c.png";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.profile_complete ? "/" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img src={ONBOARDING_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-slate-900 w-full bg-gradient-to-t from-white/90 via-white/40 to-transparent">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white grid place-items-center"><Heart className="w-5 h-5" /></div>
            <span className="font-heading font-bold text-xl">Health Guardian</span>
          </div>
          <h2 className="font-heading text-3xl font-bold mb-2 tracking-tight">Your calm daily companion for better health.</h2>
          <p className="text-slate-600 text-base">Track sugar, BP, weight, water, sleep & more — with an AI assistant powered by Claude.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white grid place-items-center"><Heart className="w-5 h-5" /></div>
            <span className="font-heading font-bold text-xl text-slate-900">Health Guardian</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">Welcome back</h1>
          <p className="text-slate-500 mb-8">Sign in to continue tracking your health journey.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="pl-9 h-11 rounded-xl bg-slate-50 border-slate-200"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="pl-9 h-11 rounded-xl bg-slate-50 border-slate-200"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="login-error">{error}</div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-500">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button
            onClick={handleGoogle}
            data-testid="google-login"
            className="w-full h-11 rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition flex items-center justify-center gap-2 font-medium text-slate-700"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#fbbc05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
            Continue with Google
          </button>

          <p className="text-sm text-slate-500 mt-6 text-center">
            New here?{" "}
            <Link to="/register" data-testid="goto-register" className="text-blue-600 font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
